import React, { useState, useEffect } from 'react';
import { MessageSquare, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import chatService from '../../services/chatService';
import { formatDistanceToNow } from 'date-fns';

/**
 * RecentConversations Component
 * Displays recent AI chats linked to a Focus item
 * Shows first user question, date, and snippet of AI response
 */
export default function RecentConversations({ focusId, userId, limit = 5 }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!focusId || !userId) return;

    const loadConversations = async () => {
      try {
        setLoading(true);
        const { data, error } = await chatService.getFocusRecentConversations(userId, focusId, limit);
        
        if (error) {
          console.error('Error loading focus conversations:', error);
          // Treat "no conversations" as empty state, not error
          setConversations([]);
        } else {
          setConversations(data || []);
        }
      } catch (err) {
        console.error('Error loading focus conversations:', err);
        // Treat any error as empty state for now
        setConversations([]);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [focusId, userId, limit]);

  const handleConversationClick = (conversationId) => {
    navigate(`/ai-chat/${conversationId}`);
  };

  if (loading) {
    return (
      <div className="space-y-2.5">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="p-3 bg-gray-50 rounded-xl animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!conversations || conversations.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-xl border border-gray-100 text-center">
        <MessageSquare size={24} className="text-gray-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-600 mb-1">No conversations yet</p>
        <p className="text-xs text-gray-500 leading-relaxed">
          Start a conversation about this Focus and it will be saved here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {conversations.map((conv) => (
        <ConversationCard 
          key={conv.conversation_id} 
          conversation={conv} 
          onClick={() => handleConversationClick(conv.conversation_id)} 
        />
      ))}
    </div>
  );
}

/**
 * Individual conversation card component
 */
function ConversationCard({ conversation, onClick }) {
  const {
    conversation_title,
    last_message_at,
    first_user_message,
    first_ai_response,
    message_count
  } = conversation;

  // Format timestamp
  const timeAgo = last_message_at 
    ? formatDistanceToNow(new Date(last_message_at), { addSuffix: true })
    : 'Unknown';

  // Truncate text to first line or 100 chars
  const truncate = (text, maxLength = 100) => {
    if (!text) return '';
    const firstLine = text.split('\n')[0];
    if (firstLine.length > maxLength) {
      return firstLine.substring(0, maxLength) + '...';
    }
    return firstLine;
  };

  return (
    <div 
      onClick={onClick}
      className="p-3 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 cursor-pointer group"
    >
      {/* Header - User question */}
      <div className="flex items-start gap-2 mb-1.5">
        <MessageSquare size={12} className="text-[#7A47F5] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-gray-900 leading-snug group-hover:text-[#7A47F5] transition-colors font-medium">
          {truncate(first_user_message || conversation_title || 'Untitled conversation', 80)}
        </p>
      </div>
      
      {/* AI response snippet (if available) */}
      {first_ai_response && (
        <div className="pl-5 mb-1.5">
          <p className="text-xs text-gray-600 leading-relaxed">
            {truncate(first_ai_response, 120)}
          </p>
        </div>
      )}
      
      {/* Footer - timestamp and message count */}
      <div className="pl-5 flex items-center gap-3 text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <Clock size={10} />
          {timeAgo}
        </span>
        <span className="text-gray-300">·</span>
        <span>{message_count} {message_count === 1 ? 'message' : 'messages'}</span>
      </div>
    </div>
  );
}

